import { ActionContext, Commit, Dispatch } from 'vuex';
import StoreUtil from '@store/StoreUtil';
import config from '@config';
import {
    CLEAR_SELECTIONS,
    SET_IS_LOADING,
    SET_PURCHASE_TYPE,
    SET_STAKE,
    TOGGLE_SYSTEM_SELECTED,
    TOGGLE_SELECTION,
    CLEAR_SELECTED_SYSTEMS,
    SET_EVENT,
    SET_IS_PAYOUT_LOCKED,
    SET_ERROR,
    SET_CANDIDATE_SELECTION,
    SET_UNDO_SELECTIONS,
    SET_UNDO_SELECTIONS_TIMER_ID,
    SET_UNDO_SYSTEMS,
    SET_SELECTED_SYSTEMS,
    SET_UNDO_STAKE,
} from './mutationTypes';
import { BetSlipPurchaseType } from '@models/shared/betSlip/betSlipEnums';
import { TicketType } from '@models/tickets/enums';
import LottoSystem from '@core/models/lottoBetSlip/LottoSystem';
import { Payout } from '@core/models/betSlip/Payout';
import ISerializedBetSlip from '@core/models/lottoBetSlip/serialization/ISerializedBetSlip';
import IValidationResult from '@core/models/betSlip/validation/IValidationResult';
import ValidationService from '@core/services/lottoBetSlip/validation/ValidationService';
import LottoOdd from '@core/models/lotto/LottoOdd';
import ILottoBetSlipState from './types';
import ServerConfigProvider from '@core/providers/configuration/ServerConfigProvider';
import { ITicketsStackState } from '@store/modules/ui/shared/ticketsStack/ticketsStackIndex';
import { IState } from '@app/store';
import Ticket from '@models/tickets/Ticket';
import { routeNames } from '@app/router';
import LottoService from '@core/services/lotto/LottoService';
import LottoEvent, { isAvailableForBetting } from '@models/lotto/LottoEvent';
import { BetSlipErrorFactory } from '@models/betSlip/validation/IBetSlipError';
import {
    CalculatedPayout,
} from '@superbet-group/betting.lib.payments';
import LottoPayoutService from '@core/services/lottoBetSlip/LottoPayoutService';

function mapToClassicPayout(payout: CalculatedPayout): Payout {
    return {
        base: payout.maxWin.base,
        bonus: payout.maxWin.bonus,
        tax: payout.maxWin.taxes.totalTax,
        final: payout.maxWin.net,
        minFinal: payout.minWin.net,
        taxPerTranche: payout.maxWin.taxes.brackets.map(bracket => ({
            value: bracket.amount,
            percentage: bracket.rate,
        })),
    };
}
export default {
    setStake(
        { commit, dispatch, rootGetters }: { commit: Commit, dispatch: Dispatch, rootGetters: any },
        stake: number
    ): void {
        commit(SET_STAKE, stake);
        dispatch('validateBetSlip');
        commit(SET_STAKE, Math.min(rootGetters['data/country/config'].lottoBetSlip.stakeMax, stake));
        dispatch('calculatePayout');
    },
    setDefaultStake(
        { dispatch, getters }: { dispatch: Dispatch, getters: any },
    ): void {
        dispatch('setStake', ServerConfigProvider.getConfig().lottoBetSlip.stakeMin);
    },
    setPurchaseType(
        { commit, dispatch }: { commit: Commit, dispatch: Dispatch },
        purchaseType: BetSlipPurchaseType
    ): void {
        commit(SET_PURCHASE_TYPE, purchaseType);
        dispatch('calculatePayout');
        dispatch('validateBetSlip');
    },
    setIsLoading: StoreUtil.createSimpleMutatorAction(SET_IS_LOADING),

    clearError: ({ commit }: { commit: Commit }) => commit(SET_ERROR, null),

    setEvent: StoreUtil.createSimpleMutatorAction(SET_EVENT),

    clear({ commit, dispatch, state }: { commit: Commit, dispatch: Dispatch, state: ILottoBetSlipState }) {
        commit(SET_UNDO_SELECTIONS, state.selections);
        commit(SET_UNDO_STAKE, state.stake);
        commit(SET_UNDO_SYSTEMS, state.selectedSystems);
        const timerId = setTimeout(
            () => {
                dispatch('_clearUndoSelections');
            },
            config.app.lottoBetSlip.undoClearDuration
        );
        commit(SET_UNDO_SELECTIONS_TIMER_ID, timerId);
        commit(CLEAR_SELECTED_SYSTEMS);
        commit(CLEAR_SELECTIONS);
        dispatch('setDefaultStake');
        commit(SET_ERROR, null);
        dispatch('calculatePayout');
        dispatch('validateBetSlip');
        dispatch('data/tickets/clearAllErrors', null, { root: true });
    },

    undoClear(
        { commit, dispatch, getters, state }:
            { commit: Commit, dispatch: Dispatch, getters: any, state: ILottoBetSlipState }
    ) {
        if (getters.undoSelections) {
            getters.undoSelections.forEach((selection: number) => dispatch('_addSelection', selection));
        }
        if (getters.undoSelectedSystems) {
            commit(SET_SELECTED_SYSTEMS, getters.undoSelectedSystems);
        }

        if (state.undoStake) {
            dispatch('setStake', state.undoStake);
        }

        dispatch('_clearUndoSelections');
    },

    async _clearUndoSelections(
        { commit, state }: { commit: Commit, state: ILottoBetSlipState }
    ) {
        if (state.undoSelectionTimerId) {
            clearTimeout(state.undoSelectionTimerId);
        }

        commit(SET_UNDO_SELECTIONS_TIMER_ID, null);
        commit(SET_UNDO_SELECTIONS, null);
    },

    clearSelections({ commit, dispatch }: { commit: Commit, dispatch: Dispatch }) {
        commit(CLEAR_SELECTIONS);
        dispatch('calculatePayout');
    },

    toggleSelection(
        { dispatch, getters }:
            { dispatch: Dispatch, getters: any },
        selection: number): void {
        const isNewSelection = (selection: number) => !getters.selections.includes(selection);
        isNewSelection(selection) ? dispatch('_addSelection', selection) : dispatch('_removeSelection', selection);
    },

    _addSelection(
        { dispatch, commit, getters }: { dispatch: Dispatch, commit: Commit, getters: any },
        selection: number
    ) {
        commit(SET_CANDIDATE_SELECTION, selection);
        dispatch('validateBetSlip');
        commit(SET_CANDIDATE_SELECTION, null);
        if (getters.error) {
            return;
        }
        commit(TOGGLE_SELECTION, selection);
        dispatch('calculatePayout');
        dispatch('validateBetSlip');
        dispatch('_clearUndoSelections');
    },

    _removeSelection({ dispatch, commit }: { dispatch: Dispatch, commit: Commit }, selection: number) {
        commit(TOGGLE_SELECTION, selection);
        dispatch('_removeUnavailableSystems');
        dispatch('calculatePayout');
        dispatch('validateBetSlip');
    },

    selectMultipleSelections(
        { dispatch, commit }: { dispatch: Dispatch, commit: Commit },
        selections: number[]): void {
        selections.forEach((selection) => commit(TOGGLE_SELECTION, selection));
        dispatch('_removeUnavailableSystems');
        dispatch('calculatePayout');
        dispatch('validateBetSlip');
        dispatch('_clearUndoSelections');
    },

    _removeUnavailableSystems({ commit, getters }: { commit: Commit, getters: any }) {
        const systemsMap = getters.selectedSystemsMap;
        const shouldRemoveSystem = (id: string) => id > getters.selectionsCount && systemsMap[id];
        Object.keys(systemsMap)
            .forEach((id) => {
                if (shouldRemoveSystem(id)) {
                    commit(TOGGLE_SYSTEM_SELECTED, { id, isSelected: true });
                }
            });
    },

    calculatePayout: (
        { commit, getters, state }:
            { commit: Commit, getters: any, state: ILottoBetSlipState }) => {
        if (state.isPayoutLocked) {
            return;
        }
        const payout = LottoPayoutService.getInstance().getPayout({
            numbersDrawn: getters.numbersDrawn,
            selectionsCount: getters.selectionsCount,
            systems: getters.payoutSystems,
            odds: getters.odds.map((odd: LottoOdd) => odd.value),
            stake: getters.stake,
            handlingFeeRate: getters.taxPercentage,
        });
        const mappedPayout = mapToClassicPayout(payout);
        commit('SET_PAYOUT', mappedPayout);
    },

    toggleSystemSelected(
        { commit, dispatch }: { commit: Commit, dispatch: Dispatch },
        system: LottoSystem) {
        commit(TOGGLE_SYSTEM_SELECTED, system);
        dispatch('calculatePayout');
    },

    submitTicket: async ({ dispatch, getters, commit }:
        { dispatch: Dispatch, getters: any, commit: Commit }) => {
        dispatch('validateBetSlip', true);
        if (getters.error || getters.selectionError) {
            return;
        }

        if (!isAvailableForBetting(getters.betSlipDTO.eventDate, getters.betSlipDTO.endBet)) {
            commit(SET_ERROR, BetSlipErrorFactory.createEventCantPlaceBets());
            return;
        }

        return dispatch(
            'data/tickets/submitTicket',
            {
                betSlip: getters.betSlipDTO,
                ticketType: TicketType.lotto,
            },
            { root: true }
        );
    },

    deserializeBetSlip(
        { commit, dispatch }: { commit: Commit, dispatch: Dispatch },
        serializedBetSlip: ISerializedBetSlip) {
        if (!serializedBetSlip) {
            return;
        }
        commit(SET_IS_PAYOUT_LOCKED, true);
        dispatch('setIsLoading', true);
        commit(SET_STAKE, serializedBetSlip.stake);
        commit(SET_PURCHASE_TYPE, serializedBetSlip.purchaseType);
        serializedBetSlip.selections.forEach((selection) => commit(TOGGLE_SELECTION, selection));
        commit(SET_EVENT, deserializeEvent(serializedBetSlip));

        Object.keys(serializedBetSlip.selectedSystems).forEach((id) =>
            commit(
                TOGGLE_SYSTEM_SELECTED,
                { id, isSelected: !serializedBetSlip.selectedSystems[id as any] })
        );

        dispatch('setIsLoading', false);
        commit(SET_IS_PAYOUT_LOCKED, false);
        dispatch('calculatePayout');
        dispatch('validateBetSlip');
    },

    validateBetSlip(
        { commit, getters }: { commit: Commit, getters: any },
        isSubmit = false
    ): void {
        commit(SET_ERROR, null);
        const validationResult: IValidationResult = ValidationService.getInstance().validate(
            getters.betSlipDTO,
            isSubmit,
        );
        if (validationResult.betSlipError) {
            commit(SET_ERROR, validationResult.betSlipError);
        }
    },

    recreateTicket: async (
        context: ActionContext<ITicketsStackState, IState>,
        ticket: Ticket
    ) => {
        if (context.getters['ui/ticketsStack/isRecreatingTicket']) {
            return;
        }

        context.dispatch('ui/ticketsStack/setIsRecreatingTicket', true, { root: true });

        const currentRoute = context.rootGetters['navigation/route'].name;
        if (routeNames.lotteryGame !== currentRoute
            && routeNames.lotteryLanding !== currentRoute
            && routeNames.lotterySystem !== currentRoute
        ) {
            context.dispatch(
                'navigation/push',
                {
                    name: routeNames.lotteryLanding,
                },
                { root: true }
            );
        }
        context.dispatch('clear');
        // TODO: set purchase type offline, in case of SSBT ticket purchase
        context.dispatch('setPurchaseType', BetSlipPurchaseType.offline);
        context.dispatch('setStake', ticket.stake);

        if (ticket.system) {
            context.commit(
                SET_SELECTED_SYSTEMS,
                ticket.system.selected.reduce(
                    (a, item) => ({ ...a, [item]: true }),
                    {}
                ),
            );
        }

        const events = await LottoService.getInstance()
            .getLottoEventDetails([parseInt(ticket.events[0].eventId, 10)]);
        const event: LottoEvent = events[0];

        if (!isAvailableForBetting(event.date, event.endBet)) {
            context.commit(SET_ERROR, BetSlipErrorFactory.createEventCantPlaceBets());
            return;
        }

        const selections = ticket.events.map((event) => parseInt(event.odd.name, 10));
        selections.forEach((selection) => context.commit(TOGGLE_SELECTION, selection));

        if (event) {
            context.commit(
                SET_EVENT,
                {
                    name: event.name,
                    numbersDrawn: event.numbersDrawn,
                    totalNumbers: event.totalNumbers,
                    id: event.id,
                    lottoId: event.lottoId,
                    endBet: event.endBet,
                    date: event.date,
                    odds: event.odds,
                    rules: event.rules
                }
            );
        }
        context.dispatch('calculatePayout');
        context.dispatch('validateBetSlip');
        context.dispatch('ui/ticketsStack/setIsRecreatingTicket', false, { root: true });
    },
};

const deserializeEvent = (serializedBetSlip: ISerializedBetSlip) => {
    if (!serializedBetSlip.event) {
        return null;
    }
    const {
        totalNumbers,
        numbersDrawn,
        name,
        eventId,
        lottoId,
        endBet,
        eventDate,
        odds,
        rules
    } = serializedBetSlip.event!;
    return {
        name,
        totalNumbers,
        lottoId,
        odds,
        numbersDrawn,
        rules,
        id: eventId,
        endBet: new Date(endBet),
        date: new Date(eventDate),
    };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/ui/lotto/betSlip/actions.ts